/**
 * Easter stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterStickersPack: ToilePack = {
    slug: 'easter-stickers',
    category: 'season',
    name: 'Easter stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'bunny', label: 'Bunny basket', subject: 'A round bunny holding a basket of eggs, a thick white die-cut border'},
        {key: 'chick', label: 'Hatching chick', subject: 'A yellow chick hatching from a cracked egg with a surprised face, a thick white die-cut border'},
        {key: 'egg', label: 'Egg with a bow', subject: 'A painted egg with stripes and dots and a small bow on top, a thick white die-cut border'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['a duckling instead', 'as a sticker sheet of three', 'with a glittery finish'],
    directionHint: 'try: a duckling instead',
    seo: {
        title: 'Easter Stickers: Bunny, Chick & Painted Egg',
        metaDescription: 'Nine die-cut Easter stickers — bunny with a basket, hatching chick, painted egg with a bow — in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter stickers: bunny, hatching chick, painted egg',
        tagline: 'Nine stickers, three motifs, three treatments each.',
        intro: 'Nine die-cut Easter stickers, each with a thick white border: a round bunny holding a basket of eggs, a yellow chick hatching from a cracked egg, and a painted egg with a small bow, in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Ready to print and cut, or drop straight onto a digital planner.',
        useCases: [
            'a printable sticker sheet for a planner or journal',
            'an Easter basket or egg-hunt favor',
            'a classroom craft or bulletin board',
            'a digital sticker for a tablet planning app',
        ],
        faq: [
            {q: 'Are these already die-cut, or do I cut them myself?', a: 'Each sticker is drawn with its white die-cut border built in, ready to print and cut out yourself with scissors or a cutting machine.'},
            {q: 'Can I print these at home?', a: 'Yes — each file is a clean isolated image on a plain background, sized to print at typical sticker-paper dimensions.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I use these in a digital planner app?', a: 'Yes — the transparent-friendly PNGs drop straight into GoodNotes, Notability, or any app that accepts image stickers.'},
        ],
    },
}
